import * as React from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

/**
 * Compatibility wrapper for react-router-dom v6/v7 (replaces deprecated withRouter).
 * Injects match, location, and history into the wrapped component.
 */
export function withRouter<P extends object>(
  Component: React.ComponentType<P & {
    match: { params: Record<string, string | undefined> };
    location: ReturnType<typeof useLocation>;
    history: { push: (path: string) => void; replace: (path: string) => void };
  }>
) {
  return function WithRouterWrapper(props: P) {
    const params = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const match = { params: params as Record<string, string | undefined> };
    const history = React.useMemo(
      () => ({
        push: (path: string) => navigate(path),
        replace: (path: string) => navigate(path, { replace: true }),
      }),
      [navigate]
    );
    return (
      <Component
        {...(props as P)}
        match={match}
        location={location}
        history={history}
      />
    );
  };
}
